import { useState, useCallback } from 'react';
import apiClient from '../api/apiClient';

export interface UseCSVExportOptions {
  endpoint: string;
  filename?: string;
  params?: Record<string, any>;
}

export interface UseCSVExportReturn {
  exportCSV: (additionalParams?: Record<string, any>) => Promise<void>;
  loading: boolean;
  error: string | null;
  getPreview: (rows?: number) => Promise<any>;
}

export const useCSVExport = ({
  endpoint,
  filename,
  params = {},
}: UseCSVExportOptions): UseCSVExportReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportCSV = useCallback(async (additionalParams?: Record<string, any>) => {
    setLoading(true);
    setError(null);
    
    try {
      // パラメータをマージ
      const allParams = { ...params, ...additionalParams };
      
      // 除外フィールドを取得
      const excludeFields = allParams.exclude_fields || [];
      delete allParams.exclude_fields;
      
      // URLSearchParamsを構築
      const urlParams = new URLSearchParams();
      urlParams.append('output_format', 'csv');
      
      // 除外フィールドをカンマ区切りで追加
      if (excludeFields.length > 0) {
        urlParams.append('exclude_fields', excludeFields.join(','));
      }
      
      // 配列パラメータの処理
      const hasArrayParams = 
        (allParams.role_type && Array.isArray(allParams.role_type)) ||
        (allParams.workshop_status && Array.isArray(allParams.workshop_status));
        
      if (hasArrayParams) {
        if (allParams.role_type && Array.isArray(allParams.role_type) && allParams.role_type.length > 0) {
          urlParams.append('role_type__in', allParams.role_type.join(','));
          delete allParams.role_type;
        }
        
        if (allParams.workshop_status && Array.isArray(allParams.workshop_status) && allParams.workshop_status.length > 0) {
          urlParams.append('workshop_status__in', allParams.workshop_status.join(','));
          delete allParams.workshop_status;
        }
      }
      
      // 残りのパラメータを追加
      Object.entries(allParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          urlParams.append(key, String(value));
        }
      });
      
      // URLを構築 - 末尾にスラッシュが必要
      const endpointWithSlash = endpoint.endsWith('/') ? endpoint : `${endpoint}/`;
      const fullUrl = `${endpointWithSlash}?${urlParams.toString()}`;
      
      const response = await apiClient.get(fullUrl, {
        responseType: 'blob',
      });
      
      // Blobを作成
      const blob = new Blob([response.data as BlobPart], { 
        type: 'text/csv; charset=utf-8' 
      });
      
      // ダウンロード用のリンクを作成
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // ファイル名を設定
      if (filename) {
        link.download = filename;
      } else {
        // Content-Dispositionヘッダーからファイル名を取得
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            link.download = filenameMatch[1];
          } else {
            link.download = 'export.csv';
          }
        } else {
          link.download = 'export.csv';
        }
      }
      
      // リンクをクリックしてダウンロード開始
      document.body.appendChild(link);
      link.click();
      
      // クリーンアップ
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 
                          err.message || 
                          'CSVダウンロードに失敗しました';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [endpoint, filename, params]);

  const getPreview = useCallback(async (rows: number = 5) => {
    setLoading(true);
    setError(null);
    
    try {
      // プレビュー用のエンドポイントがあると仮定
      const previewEndpoint = endpoint.replace('/csv_export', '/csv_export_preview');
      const response = await apiClient.post(previewEndpoint, {
        ...params,
        preview_rows: rows,
      });
      
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 
                          err.message || 
                          'プレビューの取得に失敗しました';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [endpoint, params]);

  return {
    exportCSV,
    loading,
    error,
    getPreview,
  };
};
